import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import './SolicitationDetailModal.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

export interface SolicitationHistoryItem {
  id: number;
  status: string;
  posted_date: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  last_modified_by: string | null;
  flag: boolean;
  due_date?: string | null;
  deadline_date?: string | null;
}

export interface SolicitationDetails {
  id: number;
  solicitation_number: string;
  title: string;
  status: string;
  posted_date: string | null;
  due_date: string | null;
  deadline_date: string | null;
  archive_date: string | null;
  uiLink: string | null;
  resourceLink: string | null;
  parent_short_name: string | null;
  full_parent_name: string | null;
  noticeId: string | null;
  isResponseDeadlineUpdated: boolean;
  responseDeadlineUpdatedDate: string | null;
  previousResponseDeadline: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  last_modified_by: string | null;
  flag: boolean;
}

export interface SolicitationDetailsResponse {
  success: boolean;
  solicitation_number: string;
  current: SolicitationDetails | null;
  history: SolicitationHistoryItem[];
  total_records: number;
}

interface SolicitationDetailModalProps {
  visible: boolean;
  onHide: () => void;
  data: SolicitationDetailsResponse | null;
  isLoading?: boolean;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getStatusClass = (status: string): string => {
  switch (status) {
    case 'SOLICITATION':
      return 'status-solicitation';
    case 'PRE_SOLICITATION':
      return 'status-pre-solicitation';
    default:
      return 'status-default';
  }
};

const formatStatus = (status: string): string => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Parse resourceLink which may be a single URL, JSON array, or Python list string
 */
const parseResourceLinks = (resourceLink: string | null): string[] => {
  if (!resourceLink) return [];

  // If it's already a valid URL, return as single item
  if (resourceLink.startsWith('http://') || resourceLink.startsWith('https://')) {
    // Check if it's not an array representation
    if (!resourceLink.startsWith('[')) {
      return [resourceLink];
    }
  }

  try {
    // Try parsing as JSON array
    const parsed = JSON.parse(resourceLink);
    if (Array.isArray(parsed)) {
      return parsed.filter((url: string) => typeof url === 'string' && url.startsWith('http'));
    }
  } catch {
    // Not valid JSON, try parsing Python-style list
    // Format: ['url1', 'url2', ...]
    const match = resourceLink.match(/\[([^\]]+)\]/);
    if (match) {
      const urlsStr = match[1];
      // Extract URLs from the string
      const urls = urlsStr.match(/https?:\/\/[^\s'",\]]+/g);
      if (urls) {
        return urls;
      }
    }
  }

  return [];
};

const SolicitationDetailModal: React.FC<SolicitationDetailModalProps> = ({
  visible,
  onHide,
  data,
  isLoading = false,
}) => {
  const [showResources, setShowResources] = useState(false);
  const current = data?.current;
  const history = data?.history || [];
  const resourceLinks = parseResourceLinks(current?.resourceLink || null);

  const handleLinkClick = (url: string | null, e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleResources = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowResources(!showResources);
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={null}
      className="solicitation-detail-modal"
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      style={{ width: '90vw', maxWidth: '900px' }}
    >
      {isLoading ? (
        <div className="solicitation-detail-modal-loading">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          <p>Loading solicitation details...</p>
        </div>
      ) : current ? (
        <div className="solicitation-detail-modal-content">
          {/* Header Section */}
          <div className="solicitation-detail-modal-header">
            <div className="header-main">
              <h2 className="solicitation-title">{current.title || 'Untitled Solicitation'}</h2>
              <span className={`status-badge ${getStatusClass(current.status)}`}>
                {formatStatus(current.status)}
              </span>
            </div>
            <div className="solicitation-number">
              <i className="pi pi-hashtag"></i>
              {current.solicitation_number}
            </div>
          </div>

          {/* Agency Info */}
          <div className="solicitation-agency">
            <i className="pi pi-building"></i>
            <span className="agency-full">{current.full_parent_name || 'N/A'}</span>
            {current.parent_short_name && (
              <span className="agency-short">({current.parent_short_name})</span>
            )}
          </div>

          {/* Key Dates Section */}
          <div className="solicitation-dates-grid">
            <div className="date-card">
              <div className="date-label">
                <i className="pi pi-calendar-plus"></i>
                Posted Date
              </div>
              <div className="date-value">{formatDate(current.posted_date)}</div>
            </div>
            <div className="date-card">
              <div className="date-label">
                <i className="pi pi-calendar"></i>
                Due Date
              </div>
              <div className="date-value">{formatDateTime(current.due_date)}</div>
            </div>
            {/* <div className="date-card deadline">
              <div className="date-label">
                <i className="pi pi-clock"></i>
                Deadline
              </div>
              <div className="date-value">{formatDateTime(current.deadline_date)}</div>
              {current.isResponseDeadlineUpdated && (
                <div className="deadline-updated">
                  <i className="pi pi-info-circle"></i>
                  Updated from {formatDateTime(current.previousResponseDeadline)}
                </div>
              )}
            </div> */}
          </div>

          {/* Links Section */}
          <div className="solicitation-links">
            {current.uiLink && (
              <button
                className="link-button primary"
                onClick={e => handleLinkClick(current.uiLink, e)}
              >
                <i className="pi pi-external-link"></i>
                View on SAM.gov
              </button>
            )}
            {resourceLinks.length > 0 && (
              <div className="resources-dropdown-container">
                <button className="link-button secondary" onClick={toggleResources}>
                  <i className="pi pi-file"></i>
                  View Resources ({resourceLinks.length})
                  <i className={`pi ${showResources ? 'pi-chevron-up' : 'pi-chevron-down'}`}></i>
                </button>
                {showResources && (
                  <div className="resources-dropdown">
                    {resourceLinks.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resource-link-item"
                        onClick={e => e.stopPropagation()}
                      >
                        <i className="pi pi-download"></i>
                        Resource {index + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* History Section - Horizontal Table */}
          {history.length > 0 && (
            <div className="solicitation-history">
              <h3 className="history-title">
                <i className="pi pi-history"></i>
                Status History
              </h3>
              <div className="history-table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Posted Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(item => (
                      <tr key={item.id} className={item.flag ? 'active-row' : 'inactive-row'}>
                        <td>
                          <span className={`history-status-badge ${getStatusClass(item.status)}`}>
                            {formatStatus(item.status)}
                          </span>
                        </td>
                        <td>{item.posted_date ? formatDate(item.posted_date) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer with metadata */}
          <div className="solicitation-footer">
            {current.noticeId && (
              <span className="footer-item">
                <strong>Notice ID:</strong> {current.noticeId}
              </span>
            )}
            {current.created_at && (
              <span className="footer-item">
                <strong>Created:</strong> {formatDateTime(current.created_at)}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="solicitation-detail-modal-content">
          <div className="no-data">
            <i className="pi pi-info-circle"></i>
            <p>No solicitation data available</p>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default SolicitationDetailModal;
